import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import { useResizeObserver } from '../../../main/hooks/use-resize-observer'
import Sparkline from '../../futures/components/CanvasSparkline'

interface TileProps {
  sparklineDateChanges: Record<string, any>
  sparkline: number[]
  label: string
  link: string
  boxover: string
  backgroundColor: string
  last: string
  high: string
  low: string
  change: string
  sparklineChange: number
  lastChange: string
  highlightHigh: boolean
  highlightLow: boolean
  extraMargin?: boolean
  className?: string
}

export default function ForexCryptoTile({
  link,
  boxover,
  extraMargin,
  change,
  lastChange,
  label,
  highlightLow,
  highlightHigh,
  last,
  high,
  low,
  sparkline,
  sparklineChange,
  sparklineDateChanges,
  backgroundColor,
  className,
}: TileProps) {
  const [width, setWidth] = React.useState(0)
  const containerRef = React.useRef<HTMLAnchorElement>(null)
  const ResizeObserver = useResizeObserver()
  const observerRef = React.useRef<ResizeObserver | undefined>(undefined)

  React.useEffect(() => {
    const container = containerRef.current
    if (container && ResizeObserver) {
      setWidth(container.getBoundingClientRect().width)
      observerRef.current = new ResizeObserver(
        throttle(([entry]: ResizeObserverEntry[]) => {
          setWidth(entry.contentRect.width)
        }, 100)
      )
      observerRef.current.observe(container)
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [ResizeObserver])

  return (
    <a
      ref={containerRef}
      className={classnames(
        'flex w-full flex-col justify-between overflow-hidden font-sans text-2xs text-white font-medium hover:z-10 hover:rounded hover:text-white hover:outline hover:outline-2 hover:outline-yellow-200',
        { 'ml-2': extraMargin },
        className
      )}
      href={link}
      style={{ backgroundColor }}
      data-boxover={boxover}
    >
      <div className="w-full whitespace-nowrap p-2 px-1.5">
        <div className="flex w-full items-baseline justify-between overflow-hidden drop-shadow-tile-small ldpi:drop-shadow-tile-small-ldpi">
          <div className="grow truncate uppercase leading-none">{label}</div>
          <div className="flex shrink-0 items-center pl-1 leading-none">
            {change}
            <span className="mx-1 opacity-50"> &#8226; </span>
            {lastChange}
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div className="mt-0.5 text-xl drop-shadow-tile-big font-bold xl:text-2xl">{last}</div>
          <div className="mt-1.5 text-3xs tabular-nums leading-none">
            <div
              className={classnames('-mx-0.5 flex justify-between rounded-sm p-0.5', {
                'drop-shadow-tile-small ldpi:drop-shadow-tile-small-ldpi': !highlightHigh,
                'bg-yellow-200': highlightHigh,
              })}
            >
              <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightHigh })}>
                H
              </span>
              <span className={classnames({ 'text-gray-800': highlightHigh })}>{high}</span>
            </div>
            <div
              className={classnames('-mx-0.5 flex justify-between rounded-sm p-0.5', {
                'drop-shadow-tile-small ldpi:drop-shadow-tile-small-ldpi': !highlightLow,
                'bg-yellow-200': highlightLow,
              })}
            >
              <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightLow })}>
                L
              </span>
              <span className={classnames({ 'text-gray-800': highlightLow })}>{low}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full overflow-hidden pb-0.5">
        <Sparkline
          width={Math.ceil(width)}
          height={72}
          data={sparkline}
          dateChanges={sparklineDateChanges}
          change={sparklineChange}
          changeDomain={1}
          changeOpacity={false}
          hasStickyLabels
        />
      </div>
    </a>
  )
}
